/**
 * Reusable primitives shared by every page. Buttons, cards, page headers,
 * banners, empty states, skeletons. Kept in one file because each is a
 * dozen lines and they all share the same token system.
 */

import {
  forwardRef,
  useState,
  type ReactNode,
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { IconAlert, IconArrowLeft, IconCheck, IconCopy, IconInfo } from './Icons.js';

// -------- Button -----------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const buttonBase =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium ' +
  'transition-all duration-150 ease-snappy ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
  'disabled:opacity-50 disabled:cursor-not-allowed select-none whitespace-nowrap';

const buttonSize: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-3.5 text-sm',
  lg: 'h-11 px-5 text-sm',
};

const buttonVariant: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-brand-fg hover:bg-brand-hover shadow-sm active:scale-[0.98] disabled:hover:bg-brand',
  secondary:
    'bg-surface text-ink border border-line hover:bg-surface-2 hover:border-line-strong active:scale-[0.98]',
  ghost: 'text-muted hover:text-ink hover:bg-surface-2 active:scale-[0.98]',
  danger: 'bg-danger text-white hover:opacity-90 active:scale-[0.98] disabled:hover:opacity-100',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, className = '', children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={loading || disabled}
      className={`${buttonBase} ${buttonSize[size]} ${buttonVariant[variant]} ${className}`}
      {...rest}
    >
      {loading && <Spinner size={size === 'sm' ? 12 : 14} />}
      {children}
    </button>
  );
});

// Link styled like a button. Same variants, just an <a> or react-router Link.
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  className = '',
  children,
  ...rest
}: LinkProps & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    <Link
      className={`${buttonBase} ${buttonSize[size]} ${buttonVariant[variant]} ${className}`}
      {...rest}
    >
      {children}
    </Link>
  );
}

export function AnchorButton({
  variant = 'secondary',
  size = 'md',
  className = '',
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    <a
      className={`${buttonBase} ${buttonSize[size]} ${buttonVariant[variant]} ${className}`}
      {...rest}
    >
      {children}
    </a>
  );
}

export function Spinner({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      className="animate-spin"
      aria-hidden
    >
      <path d="M21 12a9 9 0 11-9-9" />
    </svg>
  );
}

// -------- Copy-to-clipboard button -----------------------------------------

/**
 * Single source of truth for copy-to-clipboard buttons. Wraps the async
 * clipboard write in try/catch: on success shows a brief "Copied" state, on
 * failure (non-HTTPS / denied permission) shows "Copy failed" instead of
 * lying that the text was copied — important on one-time-token flows.
 */
export function CopyButton({
  text,
  label = 'Copy',
  size = 'sm',
}: {
  text: string;
  label?: string;
  size?: ButtonSize;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
    setTimeout(() => setState('idle'), 2000);
  }
  return (
    <Button variant="secondary" size={size} onClick={() => void copy()}>
      {state === 'copied' ? <IconCheck size={13} /> : <IconCopy size={13} />}
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label}
    </Button>
  );
}

// -------- Card -------------------------------------------------------------

export function Card({
  className = '',
  children,
  as: As = 'section',
}: {
  className?: string;
  children: ReactNode;
  as?: 'section' | 'div' | 'article';
}) {
  return (
    <As className={`rounded-xl border border-line bg-surface shadow-card ${className}`}>
      {children}
    </As>
  );
}

export function CardHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-line">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {description && <p className="text-muted text-xs mt-0.5">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// -------- Page header ------------------------------------------------------

export function PageHeader({
  title,
  description,
  back,
  eyebrow,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  back?: { to: string; label: string };
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-8">
      {back && (
        <Link
          to={back.to}
          className="inline-flex items-center gap-1 text-xs text-muted hover:text-ink transition-colors mb-3"
        >
          <IconArrowLeft size={14} />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && (
            <div className="text-xs uppercase tracking-wider text-muted mb-1.5 font-medium">
              {eyebrow}
            </div>
          )}
          <h1 className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="text-muted text-sm mt-1.5 max-w-2xl">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </header>
  );
}

// -------- Empty state ------------------------------------------------------

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center">
      {icon && (
        <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-surface-2 text-muted flex items-center justify-center">
          {icon}
        </div>
      )}
      <p className="font-medium text-ink">{title}</p>
      {description && <p className="text-muted text-sm mt-1 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-5 inline-flex">{action}</div>}
    </div>
  );
}

// -------- Banner (inline message) -----------------------------------------

export type BannerTone = 'info' | 'success' | 'warn' | 'error';

const bannerTone: Record<BannerTone, { wrap: string; icon: typeof IconInfo }> = {
  info: {
    wrap: 'border-info/30 bg-info-soft text-info',
    icon: IconInfo,
  },
  success: {
    wrap: 'border-ok/30 bg-ok-soft text-ok',
    icon: IconCheck,
  },
  warn: {
    wrap: 'border-warn/30 bg-warn-soft text-warn',
    icon: IconAlert,
  },
  error: {
    wrap: 'border-danger/30 bg-danger-soft text-danger',
    icon: IconAlert,
  },
};

export function Banner({
  tone = 'info',
  children,
  onDismiss,
}: {
  tone?: BannerTone;
  children: ReactNode;
  onDismiss?: () => void;
}) {
  const { wrap, icon: Icon } = bannerTone[tone];
  return (
    <div
      role={tone === 'error' || tone === 'warn' ? 'alert' : 'status'}
      className={`rounded-lg border px-3.5 py-2.5 text-sm flex items-start gap-2.5 animate-fade-in ${wrap}`}
    >
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 text-ink">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-muted hover:text-ink text-xs px-1"
          aria-label="Dismiss"
        >
          ✕
        </button>
      )}
    </div>
  );
}

// -------- Section row / definition list -----------------------------------

export function FieldRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-y-1 gap-x-6 py-3 border-b border-line last:border-b-0 px-5">
      <dt className="text-xs uppercase tracking-wide text-muted font-medium pt-1">{label}</dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  );
}

// -------- Skeleton ---------------------------------------------------------

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton rounded-md ${className}`} aria-hidden />;
}

export function PageSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    </div>
  );
}

// -------- Form field wrapper ----------------------------------------------

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-xs font-medium text-ink-2">
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted">{hint}</p>}
      {error && (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// Standard input — themed once, used everywhere.
export const inputClass =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm ' +
  'placeholder:text-faint ' +
  'hover:border-line-strong focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 ' +
  'transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

// Inline code / mono token chip
export function Mono({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <code
      className={`font-mono text-[0.85em] px-1.5 py-0.5 rounded bg-surface-2 text-ink-2 ${className}`}
    >
      {children}
    </code>
  );
}

// -------- Confirm dialog ---------------------------------------------------

export function Dialog({
  title,
  children,
  footer,
  onClose,
  busy,
}: {
  title: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  onClose: () => void;
  busy?: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-surface border border-line shadow-elevated animate-scale-in">
        <div className="px-6 pt-5 pb-4 border-b border-line">
          <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-ink-2">{children}</div>
        <div className="px-6 py-4 border-t border-line flex justify-end gap-2 bg-surface-2 rounded-b-2xl">
          {footer}
        </div>
      </div>
    </div>
  );
}
